import { useCallback, useEffect, useRef, useState } from "react";
import { Outlet, useLocation, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { posthog } from "@/lib/posthog";
import { Spinner } from "@/components/ui/Spinner";
import { CompactWidgetTeaser } from "@/components/widget/CompactWidgetTeaser";
import {
  parseWidgetLayout,
  resolvePreselectedRideType,
  type WidgetHandoff,
  type WidgetLayout as WidgetLayoutPreset,
  type WidgetRideType,
} from "@/components/widget/widgetPresets";
import { validatePrefillData, type WidgetPrefillData } from "@/types/widgetPrefill";
import { parseGa4Id } from "@/lib/ga4";
import {
  getParentOrigin,
  resolveTargetOrigin,
} from "@/lib/widgetTargetOrigin";
import type { PublicCompany } from "@/components/layouts/BookingLayout";

/**
 * Inbound-accept allowlist check (#458/#461). Inbound and outbound share the
 * same allowlist SOURCE (company.allowedEmbedOrigins) but use DIFFERENT
 * functions: this exact-membership check gates inbound prefill, while outbound
 * sends resolve a parent-matched target via resolveTargetOrigin (#474). An
 * EMPTY (or unset) allowlist means the embed is unconfigured / legacy — accept
 * any inbound origin (matching the open outbound "*" fallback).
 *
 * @param origins - The operator's configured allowed embed origins (may be empty/unset).
 * @param origin - The inbound message origin to test.
 * @returns True when the origin is allowed (or the allowlist is empty/unset).
 */
function isOriginAllowed(
  origins: string[] | undefined,
  origin: string
): boolean {
  if (!origins || origins.length === 0) return true; // unconfigured ⇒ open
  return origins.includes(origin);
}

/**
 * Chromeless layout used when the booking flow is rendered inside a
 * Business-Plus operator's website via an iframe.
 *
 * Differences from BookingLayout:
 *   - No app header / footer / navigation — the operator's site supplies
 *     all that chrome.
 *   - Background is transparent so the iframe blends in.
 *   - Posts {type:"rr-widget:resize", height} to window.parent on mount
 *     + on every height change so the embedding script can auto-resize
 *     the iframe.
 *   - Sets `--primary` from the company's branding.primaryColor so any
 *     Tailwind `*-primary` utility inside the booking flow themes to
 *     the operator's color without extra plumbing.
 *
 * Format presets (ADR-011 Decision 3), all presentation-only:
 *   - `?layout=full|compact|inline` — validated against the closed enum in
 *     widgetPresets.ts; anything unknown falls back to `full` and the raw
 *     value is never reflected into markup, attributes, analytics, or errors.
 *     `compact` shows a mini-form teaser (ride-type cards + date affordance)
 *     that expands in-frame into the full wizard with step 1 pre-answered;
 *     `inline` condenses the container padding for sidebars/footers.
 *   - `?rideType=<id>` — optional preselect, allow-list validated against the
 *     company's server-fetched ride-type list; unknown ids are silently
 *     ignored. The public create endpoint re-validates server-side anyway.
 *   - Tier gating (`allowEmbeddableWidget`) is enforced both here and on the
 *     server (#199): a non-entitled operator (a tier without the flag) renders
 *     a clear "widget isn't available" state instead of the wizard, and the
 *     public create endpoint re-asserts the same flag for any widget-origin
 *     booking — so nothing here can widen capability.
 *
 * Mounted under `/widget/:companySlug`. Re-uses BookingPage and
 * BookingConfirmationPage as nested routes; they read `company` (and the
 * optional `widgetHandoff`) from outlet context exactly like the hosted
 * booking flow does.
 *
 * @returns The widget layout
 */
export function WidgetLayout() {
  const { companySlug } = useParams<{ companySlug: string }>();
  const [searchParams] = useSearchParams();
  const { pathname } = useLocation();
  const containerRef = useRef<HTMLDivElement>(null);

  // The teaser only ever replaces the booking wizard (index route) — never
  // the confirmation child route.
  const isConfirmationRoute = pathname.endsWith("/confirmation");

  // Closed-enum validation: `layout` below is always a member of
  // WIDGET_LAYOUTS, never the raw query value.
  const layout: WidgetLayoutPreset = parseWidgetLayout(searchParams.get("layout"));
  const rideTypeParam = searchParams.get("rideType");

  // Operator GA4 passthrough (#463): the validated Measurement ID (or null).
  // parseGa4Id is the single allow-list chokepoint — the raw query value is
  // never used directly, only this normalized result. The widget iframe is on
  // our origin, so the operator's gtag must load INSIDE the frame (their
  // window.parent.gtag is unreachable cross-origin); the ID is non-PII config.
  const ga4Id = parseGa4Id(searchParams.get("ga4MeasurementId"));

  const {
    data: company,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["publicCompany", companySlug],
    queryFn: async () => {
      const response = await api.get<{ success: boolean; data: PublicCompany }>(
        `/public/companies/slug/${companySlug ?? ""}`
      );
      return response.data.data;
    },
    enabled: !!companySlug,
  });

  // The ride-type catalog is needed up-front by the compact teaser and by the
  // `?rideType=` allow-list validation — but never on the confirmation route,
  // where neither applies, and never for a non-entitled operator (#199) whose
  // widget renders the gated state, not the wizard. Same query key as
  // BookingPage, so the wizard reuses this fetch from the cache when it mounts.
  const needsRideTypes =
    !isConfirmationRoute &&
    Boolean(company?.allowEmbeddableWidget) &&
    (layout === "compact" || rideTypeParam !== null);
  const rideTypesQuery = useQuery({
    queryKey: ["rideTypes", company?.id],
    queryFn: async () => {
      const response = await api.get<{
        success: boolean;
        data: { rideTypes: WidgetRideType[] };
      }>(`/public/companies/${company?.id ?? ""}/ride-types`);
      return response.data.data.rideTypes;
    },
    enabled: !!company && needsRideTypes,
  });
  const rideTypes = rideTypesQuery.data;

  // Per-preset funnel instrumentation (names + validated enum only — no PII,
  // never the raw query value). Deliberately a per-event property and NOT
  // posthog.register(): super-properties persist in the browsing profile and
  // would taint every later non-widget event (e.g. an operator opening the
  // widget preview in a new tab, then using their dashboard). BookingPage
  // tags its own booking_step_* / booking_submitted events the same way, so
  // the funnel segments by preset end to end.
  useEffect(() => {
    if (isConfirmationRoute) return; // not a new widget view
    posthog.capture("widget_viewed", { widget_layout: layout });
  }, [layout, isConfirmationRoute]);

  // Operator GA4 passthrough (#463): when a valid Measurement ID is present,
  // bootstrap gtag and load the official gtag.js INSIDE the iframe (the only
  // place it can run — a cross-origin parent's gtag is unreachable). The
  // validated ga4Id is used ONLY as the `id=` query value (built via URL so it
  // is properly encoded) and the gtag('config', …) target — never innerHTML or
  // any other sink. send_page_view:false: the operator's own site already
  // tracks page views; the widget emits only booking-funnel events
  // (BookingPage fires rr_widget_viewed / rr_booking_step_viewed /
  // rr_booking_submitted).
  //
  // Cleanup removes ONLY the network <script> on unmount — window.gtag and
  // window.dataLayer are deliberately left in place. gtag's canonical shim is
  // meant to be process-global and is idempotent via `??=`, so persisting it is
  // harmless and lets a remount reuse the queue without re-fetching gtag.js.
  // (This asymmetry — script removed, globals kept — is intentional, not a
  // teardown gap.)
  useEffect(() => {
    if (!ga4Id) return undefined;
    // Entitlement gate (#471): a non-entitled operator renders the gated
    // "widget not available" placeholder below — never the booking funnel — so
    // their gtag.js must NOT load. Mirror the needsRideTypes-style gating: the
    // effect still runs (hooks rules intact) but no-ops for non-entitled
    // companies. The server also re-asserts the flag, so this is the only place
    // GA4 could leak for a gated widget.
    if (!company?.allowEmbeddableWidget) return undefined;

    window.dataLayer ??= [];
    const dataLayer = window.dataLayer;
    // gtag.js's canonical shim queues args onto dataLayer; gtag.js reads them
    // positionally, so the rest-param array stands in for the `arguments` the
    // official snippet pushes. Define it once (??=) so a re-mount reuses it.
    window.gtag ??= function gtag(...args: unknown[]): void {
      dataLayer.push(args);
    };
    window.gtag("js", new Date());
    window.gtag("config", ga4Id, { send_page_view: false });

    const src = new URL("https://www.googletagmanager.com/gtag/js");
    src.searchParams.set("id", ga4Id);
    const script = document.createElement("script");
    script.async = true;
    script.src = src.toString();
    document.head.appendChild(script);

    return () => {
      script.remove();
    };
  }, [ga4Id, company?.allowEmbeddableWidget]);

  // Derive the postMessage targetOrigin from the operator's allowlist (#458,
  // #474). We match the REAL embedding parent (document.referrer) against the
  // allowlist and target that exact origin — so it works for every configured
  // origin, not just origins[0] (which silently dropped messages whenever an
  // operator embedded on their 2nd+ configured site). Falls back to "*" when
  // the parent can't be resolved/matched (open / legacy behaviour). The payload
  // is non-sensitive (booleans, a height number, an opaque reservation ID), so
  // "*" is an acceptable fallback rather than a silent failure. The parent
  // origin is read once via a lazy useState initializer (document.referrer is
  // stable for the iframe's lifetime) — no ref written during render.
  const [parentOrigin] = useState(getParentOrigin);
  const targetOrigin = resolveTargetOrigin(
    company?.allowedEmbedOrigins,
    parentOrigin
  );

  // Notify the host page once when the widget is ready to display (company
  // loaded + entitled). Guard with a ref so background refetches (e.g. on
  // window focus) don't fire duplicate ready events.
  const hasPostedReady = useRef(false);
  useEffect(() => {
    if (!isLoading && company?.allowEmbeddableWidget && !hasPostedReady.current) {
      hasPostedReady.current = true;
      window.parent.postMessage({ type: "rr-widget:ready" }, targetOrigin);
    }
  }, [isLoading, company?.allowEmbeddableWidget, targetOrigin]);

  // Notify the host page when the widget cannot render — either the company
  // fetch failed or the operator isn't on an entitled tier.
  useEffect(() => {
    if (isLoading) return;
    if (!company || error) {
      // Route through the same resolved targetOrigin as the sibling
      // "unavailable" send (#464) instead of a hardcoded "*". On a fetch
      // failure there's no company → no allowlist, so resolveTargetOrigin
      // genuinely degrades to "*"; using the variable keeps the two outbound
      // error sends consistent and inherits any future resolution change.
      window.parent.postMessage({ type: "rr-widget:error", code: "fetch-failed" }, targetOrigin);
    } else if (!company.allowEmbeddableWidget) {
      window.parent.postMessage({ type: "rr-widget:error", code: "unavailable" }, targetOrigin);
    }
  }, [isLoading, company, error, targetOrigin]);

  // Compact teaser → wizard handoff. Set by an explicit teaser tap; seeded by
  // a validated `?rideType=` preselect (non-override: a static snippet param
  // must not clobber an in-progress draft's choice).
  const [teaserHandoff, setTeaserHandoff] = useState<WidgetHandoff | null>(null);

  // Inbound prefill from host page (rr-widget:prefill).
  // PII travels over postMessage instead of URL params so it never appears in
  // server access logs. All payloads are validated before being stored.
  const [widgetPrefill, setWidgetPrefill] = useState<WidgetPrefillData | null>(null);

  // Stable handler reference so the same function is passed to both
  // addEventListener and removeEventListener (required for cleanup).
  const handleInboundMessage = useCallback((event: MessageEvent) => {
    // SECURITY: only accept messages from window.parent — never from child
    // frames, same-origin scripts, or sibling iframes.
    if (event.source !== window.parent) return;

    // SECURITY: origin check for PII prefill (defense-in-depth, #461).
    // When the operator has configured allowedEmbedOrigins, reject any
    // inbound message whose origin is not in that list. Inbound-accept and
    // outbound-target share the same allowlist SOURCE (company.allowedEmbedOrigins)
    // but use DIFFERENT functions (#474): inbound does an exact-allowlist
    // membership check via isOriginAllowed(allowlist, origin), while outbound
    // (rr-widget:ready / :resize / :error) resolves a parent-matched target via
    // resolveTargetOrigin(allowlist, parentOrigin). Both read one allowlist, so
    // an operator config change updates both in lockstep. An empty allowlist
    // means the embed is unconfigured / legacy — accept any origin (same open
    // behaviour as the outbound "*").
    if (!isOriginAllowed(company?.allowedEmbedOrigins, event.origin)) {
      return;
    }

    if (typeof event.data !== "object" || event.data === null) return;
    const msg = event.data as Record<string, unknown>;
    if (msg["type"] !== "rr-widget:prefill") {
      return;
    }
    const validated = validatePrefillData(msg["data"]);
    if (validated) {
      setWidgetPrefill(validated);
    }
  }, [company?.allowedEmbedOrigins]);

  useEffect(() => {
    window.addEventListener("message", handleInboundMessage);
    return () => {
      window.removeEventListener("message", handleInboundMessage);
    };
  }, [handleInboundMessage]);

  // Auto-resize: tell the parent page how tall we are whenever our height
  // changes. The accompanying widget.js script listens for these messages
  // and resizes the iframe accordingly. Uses targetOrigin derived from the
  // operator's allowedEmbedOrigins (#458); falls back to "*" when not set.
  useEffect(() => {
    if (!containerRef.current) return undefined;

    const post = (height: number): void => {
      window.parent.postMessage(
        { type: "rr-widget:resize", height },
        targetOrigin
      );
    };

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      post(entry.contentRect.height);
    });
    observer.observe(containerRef.current);

    // Initial post in case ResizeObserver doesn't fire synchronously.
    post(containerRef.current.scrollHeight);

    return () => {
      observer.disconnect();
    };
  }, [company, targetOrigin]);

  // Wait for the catalog when a preset depends on it; if the catalog query
  // fails we fall through to the wizard, which has its own retry UI.
  if (isLoading || (needsRideTypes && !!company && rideTypesQuery.isLoading)) {
    return (
      <div className="flex min-h-[280px] items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !company) {
    return (
      <div className="flex min-h-[280px] items-center justify-center p-6">
        <div className="text-center">
          <h1 className="text-lg font-semibold text-neutral-900">
            Business not found
          </h1>
          <p className="mt-2 text-sm text-neutral-600">
            This booking widget is misconfigured. Contact the business owner.
          </p>
        </div>
      </div>
    );
  }

  // Tier gate (#199): the embeddable widget is a paid feature
  // (allowEmbeddableWidget — business_plus/business_ultimate). For a
  // non-entitled operator, render a clear gated state instead of the wizard so
  // the booking flow is never embeddable for free. The SERVER also re-asserts
  // this on POST /api/public/reservations (defense in depth), so this is purely
  // a client-side affordance — the gate cannot be bypassed by editing the DOM.
  if (!company.allowEmbeddableWidget) {
    return (
      <div className="flex min-h-[280px] items-center justify-center p-6">
        <div className="max-w-sm text-center">
          <h1 className="text-lg font-semibold text-neutral-900">
            {"This booking widget isn't available"}
          </h1>
          <p className="mt-2 text-sm text-neutral-600">
            {company.name} is not currently set up to embed online booking.
            Please contact them directly to book a ride.
          </p>
        </div>
      </div>
    );
  }

  // Allow-list validation: null unless the param matches a fetched ride type
  // and the company offers multiple types (single-type companies already
  // auto-select and skip the step).
  const preselected = resolvePreselectedRideType(rideTypeParam, rideTypes ?? []);
  const paramHandoff: WidgetHandoff | null = preselected
    ? { rideTypeId: preselected.id, override: false }
    : null;
  const widgetHandoff = teaserHandoff ?? paramHandoff;

  // Compact shows the teaser until a ride type is chosen — unless a valid
  // preselect already answered step 1, the company has fewer than two ride
  // types (the wizard then skips the Ride Type step on its own), or the
  // child route is the confirmation receipt.
  const showTeaser =
    layout === "compact" &&
    !isConfirmationRoute &&
    !widgetHandoff &&
    (rideTypes?.length ?? 0) >= 2;

  return (
    <div
      ref={containerRef}
      className={layout === "inline" ? "bg-transparent p-2" : "bg-transparent p-4"}
      style={
        {
          // Theming hook: any Tailwind `*-primary` utility used in the
          // booking flow picks up the operator's accent color through
          // the CSS variable.
          "--color-primary-600": company.branding.primaryColor,
        } as React.CSSProperties
      }
    >
      {showTeaser ? (
        <CompactWidgetTeaser
          companyName={company.name}
          timezone={company.timezone}
          rideTypes={rideTypes ?? []}
          onSelect={(rideTypeId, pickupDatetime) => {
            setTeaserHandoff({ rideTypeId, pickupDatetime, override: true });
          }}
        />
      ) : (
        <Outlet context={{ company, widgetHandoff: widgetHandoff ?? undefined, widgetPrefill, ga4Id }} />
      )}
    </div>
  );
}
